// Grouped mode's pure parts: which rows each section holds, which sections show while a search or filter is
// active, and which sections are open. Every row belongs to exactly one declared group, so search, filters
// and sort never move a row between sections: the matching set, already sorted, is split in group order.
// A row's order inside its section is its order in the sorted matching set, so sorting orders rows within
// each group and leaves the group order alone. Reordering rows or groups by drag is not part of grouped mode;
// a later reorder works on these sections (group key, rows in order) and changes nothing here.

/**
 * @typedef {{key: string, title: string, emptyText: string, expanded: boolean}} Group
 * @typedef {{group: Group, rows: object[]}} Section
 */

/**
 * The rows of each declared group, in the consumer's group order. A row whose group is not declared is
 * left out (row validation rejects it before this runs).
 * @param {{groups: Group[], groupOf: (row: object) => *}} grouped the normalized grouped configuration
 * @param {object[]} rows the sorted matching set
 * @returns {Section[]}
 */
export const groupSections = (grouped, rows) => {
  const groups = (grouped && grouped.groups) || []
  const byKey = new Map(groups.map(group => [group.key, []]))
  ;(rows || []).forEach(row => {
    const list = byKey.get(grouped.groupOf(row))
    if (list) list.push(row)
  })
  return groups.map(group => ({ group, rows: byKey.get(group.key) }))
}

/**
 * The sections that render: every one while no search or filter is active (an empty group shows its own
 * state row), and only those holding a match while one is.
 * @param {Section[]} sections
 * @param {boolean} hasQuery
 * @returns {Section[]}
 */
export const shownSections = (sections, hasQuery) =>
  hasQuery ? sections.filter(section => section.rows.length > 0) : sections

/**
 * The expanded state of every group. Outside a search each group keeps the state its header last set,
 * starting from its configured state. While a search or filter is active a group holding a match opens,
 * and a toggle then holds only for that query; clearing the query brings back the state each group held
 * before it.
 * @param {{held: Map<string, boolean>, during: {query: ?object, open: Map<string, boolean>}}} memory
 * @param {Group} group
 * @param {?object} query a token for the active query, or null when none is active. A new token stands for
 *   every committed search or filter change, so returning to an earlier query text still opens every group.
 * @returns {boolean}
 */
export const isGroupOpen = (memory, group, query) => {
  if (query) {
    const { during } = memory
    return during.query === query && during.open.has(group.key)
      ? during.open.get(group.key)
      : true
  }
  return memory.held.has(group.key)
    ? memory.held.get(group.key)
    : group.expanded !== false
}

/**
 * The memory after a group header toggles its section.
 * @returns {{held: Map<string, boolean>, during: {query: ?object, open: Map<string, boolean>}}}
 */
export const toggleGroup = (memory, group, query) => {
  const next = !isGroupOpen(memory, group, query)
  if (!query) {
    const held = new Map(memory.held)
    held.set(group.key, next)
    return { ...memory, held }
  }
  const { during } = memory
  const open = new Map(during.query === query ? during.open : [])
  open.set(group.key, next)
  return { ...memory, during: { query, open } }
}

export const EMPTY_MEMORY = Object.freeze({
  held: new Map(),
  during: { query: null, open: new Map() },
})
